/**
 * CRM Service
 * Manages complete lead-to-resident journey
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  createEnquiry,
  calculateLeadScore,
  getPipelineStage,
  calculateConversionMetrics
} from '../../data/models/Enquiry';

class CRMService {
  constructor() {
    this.storageKey = 'enquiries';
  }

  /**
   * Create new enquiry
   */
  async createEnquiry(enquiryData) {
    try {
      const enquiry = createEnquiry(enquiryData);
      enquiry.leadScore = calculateLeadScore(enquiry);
      enquiry.stage = getPipelineStage(enquiry.status);
      
      const created = LocalStorageService.addItem(this.storageKey, enquiry);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Update enquiry status (with history tracking)
   */
  async updateStatus(enquiryId, newStatus, notes = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const enquiry = all.find(e => e.id === enquiryId);
      
      if (!enquiry) {
        return { success: false, error: 'Enquiry not found' };
      }
      
      // Add to history
      const historyEntry = {
        date: new Date().toISOString(),
        fromStatus: enquiry.status,
        toStatus: newStatus,
        notes: notes || ''
      };
      
      enquiry.history = enquiry.history || [];
      enquiry.history.push(historyEntry);
      
      // Update status and stage
      enquiry.status = newStatus;
      enquiry.stage = getPipelineStage(newStatus);
      enquiry.updatedAt = new Date().toISOString();
      
      // Update conversion metrics
      const metrics = calculateConversionMetrics(enquiry);
      
      const updated = LocalStorageService.updateItem(this.storageKey, enquiryId, enquiry);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get enquiries by status/stage
   */
  async getByStatus(status = null, stage = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      let filtered = all;
      
      if (status) {
        filtered = filtered.filter(e => e.status === status);
      }
      
      if (stage) {
        filtered = filtered.filter(e => e.stage === stage);
      }
      
      // Sort by lead score (highest first) or date (newest first)
      filtered.sort((a, b) => {
        if (a.leadScore !== b.leadScore) {
          return b.leadScore - a.leadScore;
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      return { success: true, data: filtered };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get pipeline view (Kanban board data)
   */
  async getPipelineView(facilityId = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      let filtered = all;
      
      if (facilityId) {
        filtered = filtered.filter(e => 
          e.preferredFacility === facilityId || 
          (e.preferredFacilities && e.preferredFacilities.includes(facilityId))
        );
      }
      
      // Group by stage
      const pipeline = {
        enquiry: [],
        tour: [],
        application: [],
        assessment: [],
        waitlist: [],
        agreement: [],
        admission: [],
        closed: []
      };
      
      filtered.forEach(enquiry => {
        const stage = enquiry.stage || 'enquiry';
        if (pipeline[stage]) {
          pipeline[stage].push(enquiry);
        }
      });
      
      // Calculate conversion rates
      const total = filtered.length;
      const conversionRates = {
        enquiryToTour: total > 0 ? (pipeline.tour.length / total) * 100 : 0,
        tourToApplication: pipeline.tour.length > 0 ? (pipeline.application.length / pipeline.tour.length) * 100 : 0,
        applicationToAdmission: pipeline.application.length > 0 ? (pipeline.admission.length / pipeline.application.length) * 100 : 0,
        overallConversion: total > 0 ? (pipeline.admission.length / total) * 100 : 0
      };
      
      return {
        success: true,
        data: {
          pipeline,
          conversionRates,
          total,
          byPriority: {
            critical: filtered.filter(e => e.priority === 'critical').length,
            high: filtered.filter(e => e.priority === 'high').length,
            medium: filtered.filter(e => e.priority === 'medium').length,
            low: filtered.filter(e => e.priority === 'low').length
          }
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Schedule tour
   */
  async scheduleTour(enquiryId, tourData) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const enquiry = all.find(e => e.id === enquiryId);
      
      if (!enquiry) {
        return { success: false, error: 'Enquiry not found' };
      }
      
      enquiry.tourScheduled = tourData.date;
      enquiry.tourType = tourData.type || 'general';
      enquiry.tourFacility = tourData.facility || enquiry.preferredFacility;
      enquiry.updatedAt = new Date().toISOString();
      
      // Update status if needed
      if (enquiry.status === 'contacted' || enquiry.status === 'new') {
        await this.updateStatus(enquiryId, 'tour_scheduled', 'Tour scheduled');
      }
      
      const updated = LocalStorageService.updateItem(this.storageKey, enquiryId, enquiry);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Add to waitlist
   */
  async addToWaitlist(enquiryId, waitlistData) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const enquiry = all.find(e => e.id === enquiryId);
      
      if (!enquiry) {
        return { success: false, error: 'Enquiry not found' };
      }
      
      enquiry.waitlisted = true;
      enquiry.waitlistDate = new Date().toISOString().split('T')[0];
      enquiry.waitlistPosition = waitlistData.position || null;
      enquiry.waitlistPriority = waitlistData.priority || enquiry.leadScore;
      enquiry.preferredFacility = waitlistData.facility || enquiry.preferredFacility;
      enquiry.updatedAt = new Date().toISOString();
      
      await this.updateStatus(enquiryId, 'waitlisted', 'Added to waitlist');
      
      const updated = LocalStorageService.updateItem(this.storageKey, enquiryId, enquiry);
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get conversion KPIs
   */
  async getConversionKPIs(facilityId = null, startDate = null, endDate = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      let filtered = all;
      
      if (facilityId) {
        filtered = filtered.filter(e => 
          e.preferredFacility === facilityId || 
          (e.preferredFacilities && e.preferredFacilities.includes(facilityId))
        );
      }
      
      if (startDate) {
        filtered = filtered.filter(e => e.enquiryDate >= startDate);
      }
      
      if (endDate) {
        filtered = filtered.filter(e => e.enquiryDate <= endDate);
      }
      
      const total = filtered.length;
      const admitted = filtered.filter(e => e.status === 'admitted').length;
      const waitlisted = filtered.filter(e => e.waitlisted).length;
      const declined = filtered.filter(e => e.status === 'declined').length;
      
      // Calculate average conversion times
      const admittedEnquiries = filtered.filter(e => e.conversionTime);
      const avgConversionTime = admittedEnquiries.length > 0
        ? admittedEnquiries.reduce((sum, e) => sum + (e.conversionTime || 0), 0) / admittedEnquiries.length
        : 0;
      
      // Conversion by source
      const bySource = {};
      filtered.forEach(e => {
        const source = e.source || 'unknown';
        if (!bySource[source]) {
          bySource[source] = { total: 0, admitted: 0 };
        }
        bySource[source].total++;
        if (e.status === 'admitted') {
          bySource[source].admitted++;
        }
      });
      
      Object.keys(bySource).forEach(source => {
        bySource[source].conversionRate = bySource[source].total > 0
          ? (bySource[source].admitted / bySource[source].total) * 100
          : 0;
      });
      
      return {
        success: true,
        data: {
          total,
          admitted,
          waitlisted,
          declined,
          conversionRate: total > 0 ? (admitted / total) * 100 : 0,
          averageConversionTime: Math.round(avgConversionTime),
          bySource
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new CRMService();
